/**
 * Performance view (KPI tiles, date range, channel breakdown, observed vs modelled revenue, funnel, attribution
 * chain list + detail). PerformanceBody is pure (renders any state from props; used by SSR tests);
 * PerformanceView loads through a PerformanceSource (customer or agency scope).
 * States: loading, error, no connection (points to Integrations; no tiles, charts or funnel numbers), partial,
 * stale, nothing in range, data. Unmeasurable values show "Not measured"; modelled figures are always labelled.
 */
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatCurrency, formatDateTime, formatNumber, sanitizeBidi, type Locale, type Translate } from '@qm/i18n';
import { Button, DataTable, Heading, type Column } from '@qm/ui';
import {
  connectionState, metricValue, pctWidth, rangeFor, RANGE_PRESETS, TILE_METRICS,
  type ChannelSplit, type FreshnessView, type FunnelView, type Kpis, type MetricKey, type PerformanceSource, type RangePreset,
  type RecordDetail, type RecordView, type Totals,
} from './logic';
import { BffError } from '../lib/bff';
import './performance.css';

/** `code`: the API error code when known (feature_disabled, not_found, ...), so a screen can explain the refusal. */
export type Async<T> = { status: 'loading'; data?: undefined } | { status: 'error'; data?: undefined; code?: string } | { status: 'ready'; data: T };
export function errorCode(e: unknown): string | undefined {
  return e instanceof BffError ? e.envelope?.error.code ?? (e.status === 404 ? 'not_found' : undefined) : undefined;
}
export interface PerformanceData {
  freshness: Async<FreshnessView>;
  kpis: Async<Kpis>;
  channels: Async<ChannelSplit[]>;
  records: Async<RecordView[]> & { nextCursor?: string | null };
  funnel: Async<FunnelView>;
}

interface Fmt { money: (minor: number, currency: string | null) => string; num: (n: number) => string; pct: (r: number) => string; ratio: (r: number) => string; when: (iso: string) => string }
function fmtFor(locale: Locale): Fmt {
  return {
    money: (minor, currency) => formatCurrency(locale, minor, currency ?? undefined),
    num: (n) => formatNumber(locale, n),
    pct: (r) => formatNumber(locale, r, { style: 'percent', maximumFractionDigits: 1 }),
    ratio: (r) => `${formatNumber(locale, r, { maximumFractionDigits: 2 })}×`,
    when: (iso) => formatDateTime(locale, iso),
  };
}

const METRIC_LABEL: Record<MetricKey, string> = {
  spendMinor: 'attribution.kpi.spend', cpcMinor: 'attribution.kpi.cpc', cplMinor: 'attribution.kpi.cpl',
  conversionRate: 'attribution.kpi.conversionRate', revenueMinor: 'attribution.kpi.revenue', roas: 'attribution.kpi.roas',
};

function formatMetric(key: MetricKey, v: number, currency: string, f: Fmt): string {
  if (key === 'conversionRate') return f.pct(v);
  if (key === 'roas') return f.ratio(v);
  return f.money(v, currency);
}

function NotMeasured({ t }: { t: Translate }) {
  return <span className="qm-perf__nm">{t('attribution.perf.notMeasured')}</span>;
}

export function BasisTag({ basis, t }: { basis: 'observed' | 'modelled'; t: Translate }) {
  return <span className="qm-perf__basis" data-basis={basis}>{t(`attribution.basis.${basis}`)}</span>;
}

function Section({ id, title, children, testId }: { id: string; title: string; children: ReactNode; testId?: string }) {
  return (
    <section className="qm-perf__section" aria-labelledby={id} data-testid={testId}>
      <Heading level={2} id={id}>{title}</Heading>
      {children}
    </section>
  );
}

export interface PerformanceBodyProps {
  data: PerformanceData;
  t: Translate;
  locale: Locale;
  integrationsHref: string;
  preset: RangePreset;
  onPreset: (p: RangePreset) => void;
  selectedId: string | null;
  detail: Async<RecordDetail> | null;
  onSelectRecord: (id: string) => void;
  onCloseRecord: () => void;
  onMore: () => void;
  onRetry?: () => void;
  /** Agency per-client view: a 404 funnel means the grant lacks manage:billing (billing ledger figures). */
  agency?: boolean;
}

export function PerformanceBody(p: PerformanceBodyProps) {
  const { data, t, locale } = p;
  const f = useMemo(() => fmtFor(locale), [locale]);
  const uid = useId();
  const ids = { range: `${uid}-range`, tiles: `${uid}-tiles`, ch: `${uid}-ch`, rev: `${uid}-rev`, funnel: `${uid}-funnel`, rec: `${uid}-rec`, det: `${uid}-det` };

  const range = (
    <fieldset className="qm-perf__range">
      <legend className="qm-perf__legend">{t('attribution.perf.range.label')}</legend>
      {RANGE_PRESETS.map((r) => (
        <button key={r} type="button" className="qm-perf__chip" aria-pressed={p.preset === r} onClick={() => p.onPreset(r)}>
          {t(`attribution.perf.range.${r}`)}
        </button>
      ))}
    </fieldset>
  );

  if (data.freshness.status === 'loading' || data.kpis.status === 'loading') {
    return <div className="qm-perf">{range}<p className="qm-loading" role="status" aria-live="polite">{t('attribution.perf.loading')}</p></div>;
  }
  if (data.freshness.status === 'error' || data.kpis.status === 'error') {
    const disabled = (data.freshness.status === 'error' && data.freshness.code === 'feature_disabled') || (data.kpis.status === 'error' && data.kpis.code === 'feature_disabled');
    if (disabled) {
      return (
        <div className="qm-perf">
          <div className="qm-perf__notice" data-testid="perf-disabled"><p className="qm-perf__notice-text">{t('attribution.perf.disabled')}</p></div>
        </div>
      );
    }
    return (
      <div className="qm-perf">
        {range}
        <div className="qm-perf__notice" role="alert" data-tone="error">
          <p className="qm-perf__notice-text">{t('attribution.perf.error')}</p>
          {p.onRetry ? <Button onClick={p.onRetry}>{t('attribution.perf.retry')}</Button> : null}
        </div>
      </div>
    );
  }

  const fresh = data.freshness.data;
  const state = connectionState(fresh);
  if (state === 'none') {
    return (
      <div className="qm-perf">
        <div className="qm-perf__notice" data-testid="perf-no-connection">
          <Heading level={2}>{t('attribution.perf.noConnection.title')}</Heading>
          <p className="qm-perf__notice-text">{t('attribution.perf.noConnection.body')}</p>
          <Link className="qm-perf__cta" to={p.integrationsHref}>{t('attribution.perf.noConnection.cta')}</Link>
        </div>
      </div>
    );
  }

  const kpis = data.kpis.data;
  const failed = fresh.sources.filter((s) => s.status === 'failed');
  const maxSpend = Math.max(0, ...kpis.byChannel.map((c) => c.spendMinor));

  const channelCols: Column<Totals & { id: string; channel?: string }>[] = [
    { key: 'channel', header: t('attribution.perf.col.channel'), render: (r) => <bdi>{sanitizeBidi(r.channel ?? '')}</bdi> },
    { key: 'spend', header: t('attribution.kpi.spend'), numeric: true, render: (r) => (
      <span className="qm-perf__meter-cell">
        <span className="qm-perf__meter" aria-hidden="true"><span className="qm-perf__meter-fill" style={{ inlineSize: `${pctWidth(r.spendMinor, maxSpend)}%` }} /></span>
        {f.money(r.spendMinor, r.currency)}
      </span>
    ) },
    { key: 'clicks', header: t('attribution.kpi.clicks'), numeric: true, render: (r) => f.num(r.clicks) },
    { key: 'leads', header: t('attribution.kpi.leads'), numeric: true, render: (r) => (r.leads == null ? <NotMeasured t={t} /> : f.num(r.leads)) },
    { key: 'cpc', header: t('attribution.kpi.cpc'), numeric: true, render: (r) => (r.cpcMinor == null ? <NotMeasured t={t} /> : f.money(r.cpcMinor, r.currency)) },
    { key: 'cpl', header: t('attribution.kpi.cpl'), numeric: true, render: (r) => (r.cplMinor == null ? <NotMeasured t={t} /> : f.money(r.cplMinor, r.currency)) },
    { key: 'roas', header: t('attribution.kpi.roas'), numeric: true, render: (r) => (r.roas == null ? <NotMeasured t={t} /> : f.ratio(r.roas)) },
  ];

  const revenueCols: Column<ChannelSplit & { id: string }>[] = [
    { key: 'channel', header: t('attribution.perf.col.channel'), render: (r) => <bdi>{sanitizeBidi(r.channel)}</bdi> },
    { key: 'records', header: t('attribution.perf.col.journeys'), numeric: true, render: (r) => f.num(r.records) },
    { key: 'paying', header: t('attribution.perf.col.paying'), numeric: true, render: (r) => f.num(r.payingCustomers) },
    { key: 'observed', header: t('attribution.perf.col.observed'), numeric: true, render: (r) => (r.observedRevenueMinor == null ? <NotMeasured t={t} /> : f.money(r.observedRevenueMinor, r.currency)) },
    { key: 'modelled', header: t('attribution.perf.col.modelled'), numeric: true, render: (r) => (
      <span className="qm-perf__modelled-cell">{r.modelledRevenueMinor == null ? <NotMeasured t={t} /> : f.money(r.modelledRevenueMinor, r.currency)} <BasisTag basis="modelled" t={t} /></span>
    ) },
  ];

  const recordCols: Column<RecordView>[] = [
    { key: 'subject', header: t('attribution.perf.col.lead'), render: (r) => (
      <button type="button" className="qm-perf__link" aria-pressed={p.selectedId === r.id} aria-label={t('attribution.perf.records.view', { subject: sanitizeBidi(r.subjectRef) })}
        onClick={() => p.onSelectRecord(r.id)}><bdi>{sanitizeBidi(r.subjectRef)}</bdi></button>
    ) },
    { key: 'channel', header: t('attribution.perf.col.channel'), render: (r) => (r.channel ? <bdi>{sanitizeBidi(r.channel)}</bdi> : <NotMeasured t={t} />) },
    { key: 'basis', header: t('attribution.perf.col.basis'), render: (r) => <BasisTag basis={r.method} t={t} /> },
    { key: 'revenue', header: t('attribution.kpi.revenue'), numeric: true, render: (r) => (r.currency && r.revenueMinor != null ? f.money(r.revenueMinor, r.currency) : <NotMeasured t={t} />) },
    { key: 'last', header: t('attribution.perf.col.last'), render: (r) => f.when(r.lastAt) },
  ];

  return (
    <div className="qm-perf">
      {range}
      <p className="qm-perf__fresh">{fresh.asOf ? t('attribution.freshness.asOf', { time: f.when(fresh.asOf) }) : t('attribution.perf.freshness.unknown')}</p>

      {state === 'partial' ? (
        <div className="qm-perf__banner" role="status" data-testid="perf-banner-partial">
          <p className="qm-perf__notice-text">{t('attribution.banner.partial')}</p>
          {failed.length ? <ul className="qm-perf__list">{failed.map((s) => <li key={s.source}><bdi>{sanitizeBidi(s.source)}</bdi>: {t('attribution.perf.source.failed')}</li>)}</ul> : null}
          <Link className="qm-perf__link-inline" to={p.integrationsHref}>{t('attribution.perf.noConnection.cta')}</Link>
        </div>
      ) : null}
      {fresh.stale ? (
        <div className="qm-perf__banner" role="status" data-testid="perf-banner-stale"><p className="qm-perf__notice-text">{t('attribution.banner.stale')}</p></div>
      ) : null}

      {kpis.totals.length === 0 ? (
        <div className="qm-perf__notice" data-testid="perf-empty-range"><p className="qm-perf__notice-text">{t('attribution.perf.emptyRange')}</p></div>
      ) : (
        <>
          <Section id={ids.tiles} title={t('attribution.perf.tiles.title')} testId="perf-tiles">
            {kpis.totals.map((tot) => (
              <div key={tot.currency} className="qm-perf__tiles-group">
                {kpis.totals.length > 1 ? <p className="qm-perf__caption">{t('attribution.perf.tiles.currency', { currency: tot.currency })}</p> : null}
                <dl className="qm-perf__tiles">
                  {TILE_METRICS.map((m) => {
                    const v = metricValue(tot, m);
                    return (
                      <div key={m} className="qm-perf__tile">
                        <dt className="qm-perf__tile-label">{t(METRIC_LABEL[m])}</dt>
                        <dd className="qm-perf__tile-value">{v == null ? <NotMeasured t={t} /> : formatMetric(m, v, tot.currency, f)}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}
          </Section>

          <Section id={ids.ch} title={t('attribution.perf.channels.title')} testId="perf-channels">
            <DataTable caption={t('attribution.perf.channels.title')} columns={channelCols}
              rows={kpis.byChannel.map((c, i) => ({ ...c, id: `${c.channel ?? i}|${c.currency}` }))} />
          </Section>
        </>
      )}

      <Section id={ids.rev} title={t('attribution.perf.revenue.title')} testId="perf-revenue">
        <p className="qm-perf__note" data-testid="perf-modelled-note">{t('attribution.perf.modelledNote')}</p>
        {data.channels.status === 'loading' ? <p className="qm-loading" role="status">{t('attribution.perf.loading')}</p>
          : data.channels.status === 'error' ? <p className="qm-error" role="alert">{t('attribution.perf.sectionError')}</p>
          : data.channels.data.length === 0 ? <p className="qm-perf__muted">{t('attribution.chain.empty')}</p>
          : <DataTable caption={t('attribution.perf.revenue.title')} columns={revenueCols} rows={data.channels.data.map((c) => ({ ...c, id: `${c.channel}|${c.currency ?? ''}` }))} />}
      </Section>

      <Section id={ids.funnel} title={t('attribution.funnel.title')} testId="perf-funnel">
        {data.funnel.status === 'loading' ? <p className="qm-loading" role="status">{t('attribution.perf.loading')}</p>
          : data.funnel.status === 'error' ? (p.agency && data.funnel.code === 'not_found'
            ? <p className="qm-perf__muted" data-testid="perf-funnel-needs-billing">{t('attribution.perf.funnel.needsBilling')}</p>
            : <p className="qm-error" role="alert">{t('attribution.perf.sectionError')}</p>)
          : <Funnel funnel={data.funnel.data} t={t} f={f} />}
      </Section>

      <Section id={ids.rec} title={t('attribution.chain.title')} testId="perf-records">
        {data.records.status === 'loading' ? <p className="qm-loading" role="status">{t('attribution.perf.loading')}</p>
          : data.records.status === 'error' ? <p className="qm-error" role="alert">{t('attribution.perf.sectionError')}</p>
          : data.records.data.length === 0 ? <p className="qm-perf__muted">{t('attribution.chain.empty')}</p>
          : (
            <>
              <DataTable caption={t('attribution.chain.title')} columns={recordCols} rows={data.records.data} />
              {data.records.nextCursor ? <Button onClick={p.onMore}>{t('attribution.perf.records.more')}</Button> : null}
            </>
          )}
        {p.selectedId && p.detail ? <ChainDetail id={ids.det} detail={p.detail} t={t} f={f} onClose={p.onCloseRecord} /> : null}
      </Section>
    </div>
  );
}

function Funnel({ funnel, t, f }: { funnel: FunnelView; t: Translate; f: Fmt }) {
  const s = funnel.stages;
  const steps: [string, number][] = [
    ['attribution.funnel.visits', s.visits], ['attribution.funnel.checkouts', s.checkouts],
    ['attribution.funnel.paid', s.paidConversions], ['attribution.funnel.reportAccess', s.reportAccess],
  ];
  const max = Math.max(0, ...steps.map(([, n]) => n));
  return (
    <>
      <ol className="qm-perf__funnel">
        {steps.map(([k, n]) => (
          <li key={k} className="qm-perf__funnel-step">
            <span className="qm-perf__funnel-label">{t(k)}</span>
            <span className="qm-perf__meter" aria-hidden="true"><span className="qm-perf__meter-fill" style={{ inlineSize: `${pctWidth(n, max)}%` }} /></span>
            <span className="qm-perf__funnel-value">{f.num(n)}</span>
          </li>
        ))}
      </ol>
      {funnel.paidRevenue.length ? (
        <p className="qm-perf__muted">{t('attribution.perf.funnel.net')}: {funnel.paidRevenue.map((r) => f.money(r.netMinor, r.currency)).join(' · ')}</p>
      ) : null}
      <p className="qm-perf__muted">
        {t('attribution.funnel.excluded')}: {t('attribution.funnel.excluded.test')} {f.num(funnel.excluded.testMode)} · {t('attribution.funnel.excluded.free')} {f.num(funnel.excluded.free)} · {t('attribution.funnel.excluded.complimentary')} {f.num(funnel.excluded.complimentary)}
      </p>
      <p className="qm-perf__muted">{funnel.reconciliation.matched ? t('attribution.funnel.reconciled') : t('attribution.funnel.mismatch')}</p>
      <p className="qm-perf__note" data-testid="perf-funnel-footnote">{t('attribution.perf.funnel.footnote')}</p>
    </>
  );
}

function ChainDetail({ id, detail, t, f, onClose }: { id: string; detail: Async<RecordDetail>; t: Translate; f: Fmt; onClose: () => void }) {
  return (
    <div className="qm-perf__detail" role="region" aria-labelledby={id} data-testid="perf-record-detail">
      <div className="qm-perf__detail-head">
        <Heading level={3} id={id}>
          {detail.status === 'ready' ? t('attribution.perf.detail.title', { subject: sanitizeBidi(detail.data.subjectRef) }) : t('attribution.chain.title')}
        </Heading>
        <Button onClick={onClose}>{t('attribution.perf.detail.close')}</Button>
      </div>
      {detail.status === 'loading' ? <p className="qm-loading" role="status">{t('attribution.perf.loading')}</p>
        : detail.status === 'error' ? <p className="qm-error" role="alert">{t('attribution.perf.sectionError')}</p>
        : (
          <>
            {detail.data.method === 'modelled' ? <p className="qm-perf__note">{t('attribution.basis.modelledHelp')}</p> : null}
            <ol className="qm-perf__steps">
              {detail.data.steps.map((s, i) => (
                <li key={`${s.stage}-${i}`} className="qm-perf__step">
                  <span className="qm-perf__step-stage">{t(`attribution.stage.${s.stage}`)}</span>
                  <BasisTag basis={s.basis} t={t} />
                  <span className="qm-perf__muted">{f.when(s.occurredAt)}</span>
                  {s.amountMinor != null && s.currency ? <span>{f.money(s.amountMinor, s.currency)}</span> : null}
                  <span className="qm-perf__muted">{t('attribution.perf.detail.source', { module: sanitizeBidi(s.sourceModule) })}</span>
                </li>
              ))}
            </ol>
          </>
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- stateful wrapper
function useAsync<T>(load: () => Promise<T>, deps: unknown[]): [Async<T>, () => void] {
  const [state, setState] = useState<Async<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    load().then((data) => { if (live) setState({ status: 'ready', data }); }, (e: unknown) => { if (live) setState({ status: 'error', code: errorCode(e) }); });
    return () => { live = false; };
  }, [...deps, tick]);
  return [state, useCallback(() => setTick((n) => n + 1), [])];
}

export function PerformanceView({ source, t, locale, integrationsHref, now = () => new Date(), agency = false }: {
  source: PerformanceSource; t: Translate; locale: Locale; integrationsHref: string; now?: () => Date; agency?: boolean;
}) {
  const [preset, setPreset] = useState<RangePreset>('30d');
  const range = useMemo(() => rangeFor(preset, now()), [preset]);
  const [freshness, reloadF] = useAsync(async () => (await source.freshness()).data, [source]);
  const [kpis, reloadK] = useAsync(async () => (await source.kpis(range)).data, [source, range]);
  const [channels] = useAsync(async () => (await source.channels(range)).data, [source, range]);
  const [funnel] = useAsync(async () => (await source.funnel(range)).data, [source, range]);
  const [records, setRecords] = useState<PerformanceData['records']>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    setRecords({ status: 'loading' });
    source.records({ limit: 25 }).then(
      (r) => { if (live) setRecords({ status: 'ready', data: r.data, nextCursor: r.meta?.nextCursor ?? null }); },
      () => { if (live) setRecords({ status: 'error' }); },
    );
    return () => { live = false; };
  }, [source]);
  const onMore = useCallback(() => {
    if (records.status !== 'ready' || !records.nextCursor) return;
    const prev = records.data;
    source.records({ limit: 25, cursor: records.nextCursor }).then(
      (r) => setRecords({ status: 'ready', data: [...prev, ...r.data], nextCursor: r.meta?.nextCursor ?? null }),
      () => setRecords({ status: 'error' }),
    );
  }, [records, source]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Async<RecordDetail> | null>(null);
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let live = true;
    setDetail({ status: 'loading' });
    source.record(selectedId).then((r) => { if (live) setDetail({ status: 'ready', data: r.data }); }, () => { if (live) setDetail({ status: 'error' }); });
    return () => { live = false; };
  }, [selectedId, source]);

  return (
    <PerformanceBody data={{ freshness, kpis, channels, records, funnel }} t={t} locale={locale} integrationsHref={integrationsHref} agency={agency}
      preset={preset} onPreset={setPreset} selectedId={selectedId} detail={detail} onSelectRecord={setSelectedId}
      onCloseRecord={() => setSelectedId(null)} onMore={onMore} onRetry={() => { reloadF(); reloadK(); }} />
  );
}
